import React, {useEffect, useState} from "react";
import {useNavigate, useParams} from "react-router-dom";
import useDownload from "../../../hooks/useDownload";
import {MasterClassService} from "../../../services/masterClassService/masterClassService";
import Loader from "../../../components/common/Loader";
import ProgressBar from "@ramonak/react-progress-bar";
import {IoCheckmark} from "react-icons/io5";
import MasterClassLessonsList from "./MasterClassLessonsList";
import notFound from "../../../assets/not-found/not_found.png";
import DiscussionsCommentList from "../../../components/DiscussionsCommentList";
import MasterClassCompleteInfoModal from "./modals/MasterClassCompleteInfoModal";
import {PageErrorMessage} from "../../../components/PageErrorMessage";
import UserDetails from "../../../components/UserDetails";
import TextEditorContent from "../../../components/TextEditorContent";
import CustomBreadcrumbs from "../../../components/CustomBreadcrumbs/CustomBreadcrumbs";
import Swal from "sweetalert2";
import {Rating} from "@mui/material";
import usePusher from "../../../hooks/usePusher";
import {useSelector} from "react-redux";
import {toast} from "react-toastify";
import SpeakerBio from "./SpeakerBio";
import Tags from "../../../components/Tags/Tags";
import MasterClassTrailerModal from "./modals/MasterClassTrailerModal";
import {FaCirclePlay} from "react-icons/fa6";
import {CiStar} from "react-icons/ci";
import {FaStar} from "react-icons/fa";
import AddToPlaylistModal from "./modals/AddToPlaylistModal";
import {playlistService} from "../../../services/playlistsService/playlistService";

const InstitutionUserMasterClassDetails = () => {
    const [isLoading, setIsLoading] = useState(true);
    const [masterClassDetails, setMasterClassDetails] = useState(null);
    const { id } = useParams();
    const downloadFile = useDownload();
    const navigate = useNavigate();
    const [showCompletionModal, setShowCompletionModal] = useState(false);
    const pusher = usePusher();
    const [error, setError] = useState(null);
    const authUser = useSelector((state) => state?.auth?.auth);
    const [isCertificateLoading, setIsCertificateLoading] = useState(false);
    const [showTrailer, setShowTrailer] = useState(false);
    const [showPlaylists, setShowPlaylists] = useState(false);

    const masterClassDetailsData = async () => {
        setIsLoading(true);
        try {
            await MasterClassService.getSingleMasterClassData(id).then((response) => {
                setMasterClassDetails(response?.data);

                if (response?.data.all_lessons_completed && !response?.data.is_completed) {
                    setShowCompletionModal(true);
                }
            });

            setIsLoading(false);
        } catch (error) {
            setError({
                statusCode: error.response.status,
                message: error.response.data.message,
            });
            setIsLoading(false);
        }
    };

    // useEffect of call all api function
    useEffect(() => {
        masterClassDetailsData();
    }, [id]);

    const renderAnswer = (answer) => {
        const linkRegex = /(https?:\/\/[^\s]+)/g;
        const parts = answer.split(linkRegex);
        return parts.map((part, index) => {
            if (part.match(linkRegex)) {
                return (
                    <a key={index} href={part} target="_blank" rel="noopener noreferrer" className="fw-bold">
                        {part}
                    </a>
                );
            } else {
                // Remove HTML tags from the text
                const textWithoutTags = part.replace(/(<([^>]+)>)/gi, "");
                return <span key={index}>{textWithoutTags}</span>;
            }
        });
    };

    const handleCompleteConfirmation = () => {
        Swal.fire({
            title: "Are you sure?",
            text: "Are you sure you have finished studying the masterclass?",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#42c87f",
            cancelButtonColor: "#3085d6",
            confirmButtonText: "Complete",
        }).then((result) => {
            if (result.isConfirmed) {
                handleComplete();
            }
        });
    };

    const handleComplete = async () => {
        try {
            const updatedMasterclass = await MasterClassService.watchAllLessons(id);

            setMasterClassDetails(updatedMasterclass.data);

            await Swal.fire("Completed!", "All lessons has been completed", "success");
        } catch (err) {
            await Swal.fire("Error!", "Failed to complete lessons.", "error");
        }
    };

    const handleDownloadPlaybook = async () => {
        if (masterClassDetails.playbook_pdf) {
            const filename = (masterClassDetails?.name || "playbook_file").replace(/[^\w\s]/g, "_").replace(/\s+/g, "_");
            await downloadFile(masterClassDetails.playbook_pdf, filename);
            await MasterClassService.playbookDownloadLog(id);
        }
    };

    const handleCloseCompletionModal = () => {
        setShowCompletionModal(false);
    };

    const handleRateMasterClass = async (ratingValue) => {
        try {
            const updatedMasterclass = await MasterClassService.rateMasterClass(id, ratingValue);
            setMasterClassDetails(updatedMasterclass.data);
            Swal.fire("Thanks!", "Masterclass has been rated.", "success");
        } catch (err) {
            Swal.fire("Error!", "Failed to rate the masterclass.", "error");
        }
    };

    useEffect(() => {
        if (pusher) {
            const channel = pusher.subscribe('masterclass-survey-completed.' + authUser.id);
            channel.bind('masterclass-survey-completed', function(data) {
                setMasterClassDetails((prevState) => ({
                    ...prevState,
                    is_completed: true,
                }));
                toast.success("Masterclass has been completed successfully.");
                setShowCompletionModal(false);
            });
        }
    }, [pusher]);

    const handleViewCertificate = async () => {
        navigate('/master-classes/certificates/' + masterClassDetails?.certificate?.id);
    };

    const handleAddToPlaylist = () => {
        setShowPlaylists(true);
    };

    const handleRemoveFromPlaylist = async () => {
        try {
            let updatedMasterclass = null;
            if (masterClassDetails?.playlist_id) {
                updatedMasterclass = await playlistService.removeMasterclassFromPlaylist(masterClassDetails?.playlist_id, masterClassDetails?.id);
            } else {
                updatedMasterclass = await MasterClassService.removeFromFavorite(masterClassDetails?.id);
            }
            setMasterClassDetails(updatedMasterclass.data);
            toast.success("Masterclass has been successfully removed from current playlist.");
        } catch (err) {
            toast.error("Error while trying to remove from playlist.");
        }
    };

    if (error) {
        return <PageErrorMessage
            error={error}
            entityTitle="Masterclass"
        />;
    }

    const pages = masterClassDetails?.intersected_programs.length === 1
        ? [{
            title: masterClassDetails?.intersected_programs[0].title,
            to: `/programs/${masterClassDetails?.intersected_programs[0].id}`
        }]
        : [{
            title: 'Back to Program',
            isMultiple: true,
            items: masterClassDetails?.intersected_programs.map((program) => ({
                title: program.title,
                to: `/programs/${program.id}`
            }))
        }];

    return (
        <>
            {isLoading ? (
                <>
                    <Loader/>
                </>
            ) : (
                <>
                    <div className="container m-4">
                        <div className="row">
                            {masterClassDetails?.intersected_programs.length !== 0 &&
                                <CustomBreadcrumbs
                                    currentPageTitle={masterClassDetails?.name}
                                    pages={pages}
                                />
                            }
                        </div>
                        <div className="row mb-5">
                            <div className="col-md-6 mb-4 mb-md-0">
                                <h1>{masterClassDetails?.name}</h1>
                                {masterClassDetails?.tags.length > 0 &&
                                    <div className="mt-2 mb-2">
                                        <Tags
                                            tags={masterClassDetails?.tags}
                                        />
                                    </div>
                                }
                                <TextEditorContent
                                    text={masterClassDetails?.description}
                                />
                                <div className="d-flex mt-3">
                                    <button
                                        className="btn btn-primary-info me-2"
                                        onClick={() => navigate('/lessons/' + masterClassDetails?.first_lesson)}
                                        disabled={masterClassDetails?.first_lesson === null}
                                    >
                                        Start Watching
                                    </button>
                                    {masterClassDetails?.playbook_pdf &&
                                        <button className="btn btn-primary me-2"
                                                disabled={!masterClassDetails?.playbook_pdf}
                                                onClick={handleDownloadPlaybook}>Download Playbook</button>
                                    }
                                </div>
                                {masterClassDetails?.is_completed &&
                                    <div className="d-flex mt-3">
                                        <button
                                            className="btn btn-primary me-2"
                                            onClick={handleViewCertificate}
                                        >
                                            View Your Certificate
                                        </button>
                                    </div>
                                }
                                <div className="mt-3">
                                    <div className="fs-6 lh-base fw-bolder">
                                        Masterclass Progress
                                    </div>
                                    <ProgressBar completed={masterClassDetails?.progress} bgColor="#1c64f2"
                                                 baseBgColor="#374151"
                                                 className={`w-100 progressbar-count ${masterClassDetails?.progress > 5 ? '' : 'progressbar-count__hide-value'}`}/>
                                </div>
                                {(masterClassDetails?.progress !== 100) &&
                                    <button className="btn btn-success mt-3" onClick={handleCompleteConfirmation}>I’ve already watched this</button>
                                }
                                {(!masterClassDetails?.is_completed && masterClassDetails?.progress === 100) &&
                                    <div className="d-flex mt-3">
                                        <button className="btn btn-secondary me-2" disabled={true}>
                                            Already watched <IoCheckmark/>
                                        </button>
                                        <button
                                            className="btn btn-primary"
                                            onClick={() => setShowCompletionModal(true)}
                                        >
                                            Complete for credit
                                        </button>
                                    </div>
                                }
                                {masterClassDetails?.is_completed &&
                                    <button className="btn btn-secondary d-flex align-center mt-3" disabled={true}>
                                        Completed <IoCheckmark/>
                                    </button>
                                }
                                <div className="mt-3 rating-title">
                                    Rate this masterclass:
                                </div>
                                <Rating value={masterClassDetails?.rating}
                                        onChange={(event, value) => handleRateMasterClass(value)}
                                        sx={{
                                            '& .MuiRating-iconEmpty': {
                                                color: 'white',
                                            },
                                        }}
                                />
                                <div className="mt-3">
                                    {masterClassDetails?.lessons.length > 0 && !masterClassDetails?.is_available &&
                                        <MasterClassLessonsList
                                            lessons={masterClassDetails?.lessons}
                                            withProgress={false}
                                            isPreview={true}
                                            previewMessage={"Renew your membership for continued access"}
                                        />
                                    }
                                    {masterClassDetails?.lessons.length > 0 && masterClassDetails?.is_available &&
                                        <MasterClassLessonsList
                                            lessons={masterClassDetails?.lessons}
                                        />
                                    }
                                    {masterClassDetails?.lessons.length === 0 &&
                                        <div
                                            className="w-100 mt-3 rounded-4 text-center d-flex flex-column align-items-center justify-content-center"
                                            style={{height: "500px"}}>
                                            <img crossOrigin="anonymous" src={notFound} alt="data not found" width={300}/>
                                            <h3>No Lessons Added</h3>
                                            <p className="fs-5">No lessons available at the moment.</p>
                                        </div>
                                    }
                                </div>
                            </div>

                            <div className="col-md-6">
                                <div className="master-class__image">
                                    <img
                                        src={masterClassDetails?.cover_image}
                                        alt={masterClassDetails?.name}
                                        className="img-fluid"
                                    />
                                </div>
                                <div className="d-flex justify-content-center flex-wrap">
                                    {!masterClassDetails?.in_playlists &&
                                        <div className="d-flex justify-content-center" onClick={handleAddToPlaylist}>
                                            <div className="trailer-button mt-3">
                                                <CiStar fill="#8a6845" className="mr-5 fs-5"/>
                                                Add to Playlist
                                            </div>
                                        </div>
                                    }
                                    {masterClassDetails?.in_playlists &&
                                        <div className="d-flex justify-content-center" onClick={handleRemoveFromPlaylist}>
                                            <div className="trailer-button mt-3">
                                                <FaStar fill="#8a6845" className="mr-5 fs-5"/>
                                                Remove from current Playlist
                                            </div>
                                        </div>
                                    }
                                    {(masterClassDetails?.trailer_link || masterClassDetails?.trailer_url) &&
                                        <div className="d-flex justify-content-center" onClick={() => setShowTrailer(true)}>
                                            <div className="trailer-button mt-3">
                                                <FaCirclePlay fill="#8a6845" className="mr-5 fs-5" style={{borderRadius: "50%"}}/>
                                                Watch Trailer
                                            </div>
                                        </div>
                                    }
                                </div>
                                <div className="master-class__speaker-info p-3 mt-3">
                                    <div className="d-flex justify-content-center align-items-center flex-column">
                                        <div onClick={() => navigate(`/community/users/${masterClassDetails?.speaker.id}`)}>
                                            <UserDetails user={masterClassDetails?.speaker} />
                                        </div>
                                        <div className="mt-1">
                                            {masterClassDetails?.speaker?.bio && <SpeakerBio bio={masterClassDetails.speaker.bio} />}
                                        </div>
                                    </div>
                                </div>
                                <div className="master-class__comments mt-3">
                                    <DiscussionsCommentList
                                        path={`master-classes/${masterClassDetails?.id}`}
                                        relatedTitlesToDisplay={['lessons']}
                                        highlightingCompareId={masterClassDetails?.speaker.id}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                    {showCompletionModal &&
                        <MasterClassCompleteInfoModal
                            handleClose={handleCloseCompletionModal}
                            masterClass={masterClassDetails}
                            onUpdateMasterClass={(updatedMasterClass) => setMasterClassDetails(updatedMasterClass)}
                        />
                    }
                    {showTrailer &&
                        <MasterClassTrailerModal
                            onClose={() => setShowTrailer(false)}
                            masterClass={masterClassDetails}
                        />
                    }
                    {showPlaylists &&
                        <AddToPlaylistModal
                            onClose={() => setShowPlaylists(false)}
                            masterClass={masterClassDetails}
                            onMasterclassUpdate={(updatedMasterClass) => setMasterClassDetails(updatedMasterClass)}
                        />
                    }
                </>
            )}
        </>
    );
};

export default InstitutionUserMasterClassDetails;